import {callIfFunction, isSmallDevice, parseDate} from '@startlibs/utils';
import {useRefState, useToggle, WithLazyResource} from '@startlibs/core'
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled, { createGlobalStyle } from 'styled-components'
import {
  CoverFooter,
  CoverMainWrapper,
  CoverNote,
  CoverTitle,
  REPORT_HEADERS,
  ReportByPurview,
  ReportField
} from '../../admin/steps/finalReport/reportPages/ReportFrontCover';
import {DATE_FORMAT_STR, MonthDayYear} from '../../enums/ReportDateFormat'
import {ExpertProfileItem} from '../../admin/steps/finalReport/reportPages/ReportExpertsProfiles'
import {InsertedSignaturesWrapper,} from '../../expert/ExpertSignatureManagement';
import {LOGO_RIGHT_INFO_LEFT} from '../../enums/ReportHeaderLayout'
import {NotProvided} from '../../admin/steps/ExpertAssignmentStep';
import {SignatureItem} from '../../admin/steps/finalReport/reportPages/ReportSignatures'
import {TopSectionHeading,} from '../../admin/steps/finalReport/FinalReportEdit';
import {calculateAge, formatDateNoUTC, getReportResource, isRichText, safeNewLineToDoubleBr} from '../../utils/utils';
import {clearPagination, getEmptySpace, getLastDiff, getLineHeight, getPageBreak, getSeparators} from './utils'
import {i18nDateMessages} from '../../UIConfig/i18nDateMessages'
import {isStrEmpty} from '../../utils'
import {FormattedMessage, IntlProvider, useIntl} from 'react-intl'
import {RichTextDisplay} from '../../components/RichTextDisplay';
import {sanitizeHtml} from '../../utils/sanitizeHtml';
import {
  BackCoverMainWrapper,
  BackCoverNotesWrapper,
  MarketingMessage,
  ThankNote
} from '../../admin/steps/finalReport/reportPages/ReportBackCover';
import {defaultDateFormat} from "../../utils/dateFormat";
import {lazyLocale} from "../../components/I18NProvider";
import {Button, Icon, Loading} from "@startlibs/components";

const PageResizerContainer = styled.div`
  overflow: hidden;
  .report-page {
    ${isSmallDevice() ? "margin: 0 0;" : ""}
    .page-break:after {
      background-color: #f2f2f2;
    }
    .report-footer {
      position: absolute;
      bottom: 0mm;
      right: 0mm;
      left: 0mm;
      border: 0px;
      font-size: 12px;
      text-align: left;
    }
  }
  .page-counter {   
      z-index: 20;
  }
  .clear-border {
    position: absolute;
    z-index: 10;
    // border: 4px solid white;
    border-top: 0;
    border-bottom: 0;
    left: 74px;
    right: 74px;
    background: white;
  }
  .clear-border-top {
    height: 95px;
    margin-top: -95px;
    background: linear-gradient(0deg, white 80%, transparent 20%);
  }
  .clear-border-bottom {
    height: 120px;
    margin-top: 2rem;    
  }
  @media print {
    .clear-border-top {
      height: 95px;
      margin-top: -95px;
      background: transparent;
    }
    .clear-border-bottom {
      height: 120px;
      margin-top: 0rem;    
    }
  }
`

const PAPER_SIZES = {
  A4: [8.27, 11.69],
  LETTER: [8.5, 11]
}

const PrintReportStyle = createGlobalStyle`
  @page {
    margin: 0;
  }
`

const FullPage = styled.div`
  position: absolute;
  top: 0;
  left:0;
  right:0;
  height: ${props => (props.paperSize[1] * 96) + "px"};
  padding: 20mm 20mm 25mm 20mm;
  .cover-filler {
    position: relative;
    height: 100%;
    width: 100%;
  }
`


const ZoomableContainer = styled.div`
  ${props =>
    props.fullScreen ? `
      width: 100vw;
      top:0;
      left:0;
      height: 100vh;
      position: fixed;
      z-index:10000;                 
      `:`
      width: calc(100vw - 2rem);
      height: 60vh;
      position: relative;
    `}
  overflow: scroll;
`
const ZoomButton = styled(Button)` 
    position: absolute;
    top: 1rem;
    right: 1rem;
    z-index: 10;
    width: 5rem;
    height: 5rem;
    ${Icon} {
      font-size: 36px;
    }
    ${props => props.isZoomed && `
      position: fixed;  
    `}
`

const ReportOverlay = styled.div`
  position: absolute;
  height: 100%;
  width: 104%;
  background: #f2f2f2;
  z-index: 10;
`;

const ReportOverlayTopDiv = styled.div`
  width: 100%;
  height: 360px;
  margin-top: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewComponent = (props) => {
  const preferredLanguage = props.preferredLanguage || "en-US"
  const zoom = useToggle(0)
  return <WithLazyResource value={lazyLocale.use(preferredLanguage)} >{([,messages]) =>
    <IntlProvider messages={messages} locale={preferredLanguage} defaultLocale="en-US">
      {
        isSmallDevice()
        ? <>
            <ZoomableContainer fullScreen={zoom.isOpen>0}>
              {zoom.isOpen ?
                <ZoomButton isZoomed icon="zoom-out" highlight onlyIcon rounded onClick={() => zoom.openWith(i => Math.max(0,i-1))}/>
                :
                <ZoomButton icon="zoom-in" highlight onlyIcon rounded onClick={() => zoom.openWith(i => Math.min(3,i+1))}/>
              }
              <LocalizedPreview {...props} zoomDelta={zoom.isOpen}/>
          </ZoomableContainer>
          </>
        : <LocalizedPreview {...props}/>
      }
    </IntlProvider>
  }</WithLazyResource>
}

const previewPropsEqual = (prevProps, nextProps) => _.isEqual(prevProps, nextProps)

export const Preview = React.memo(PreviewComponent, previewPropsEqual)

const LocalizedPreview = (
  {
    previewReady,
    organization,
    report,
    resourceOrigin = "",
    paperSize = PAPER_SIZES.LETTER,
    isPdfPrinter,
    zoomDelta
  }) => {

  const intl = useIntl()
  const hasCoverPages = true
  const reportData = report.reportData || {}
  const reportLayout = reportData?.reportLayout || {headerLayout: LOGO_RIGHT_INFO_LEFT,dateFormat:MonthDayYear}
  const disclaimer = reportLayout.disclaimerText && !isStrEmpty(report.reportData.disclaimer || "") && report.reportData.disclaimer
  const patientDoB = report.patientInfo.dob && parseDate(report.patientInfo.dob, new Date(), "MM-dd-yyyy")

  const clinicalSummary = (reportLayout.clinicalSummary && !isStrEmpty(report.clinicalSummary))
    ? [{label:intl.formatMessage({defaultMessage:'Reason for seeking medical opinion:',description:'Preview clinical summary label'}),value:report.clinicalSummary}]
    : []

  const caseSummary = (!organization.hideCaseSummaryFromReport && !reportLayout.hideCaseSummary && !isStrEmpty(report.caseSummary))
    ? [{label:intl.formatMessage({defaultMessage:'Clinical summary:',description:'Preview case summary label'}),value:report.caseSummary}]
    : []

  const additionalFields = reportLayout.additionalFields
    ? _.filter(({hide}) => !hide,report.additionalFields).map(({title,content})=> ({label:title,value:content}))
    : []

  const questionsAndAnswers = reportLayout.questionsConsultant
    ? _.filter(({hide})=>!hide,report.questionsConsultant)
    : []


  const visibleExperts = reportLayout.expertProfileDetails
    ? _.filter((expertReview) => !expertReview?.hide,report.expertReviews)
    : []
  const signatures = reportLayout.expertSignatures
    ? _.filter(({hide})=> !hide,report.signatures)
    : []

  const lastPageFooter = true

  const isLogoLoaded = useToggle()
  const isPreviewDoneRef = useRef(false)

  const fieldsContainer = useRef()
  const compactHeaderContainer = useRef()
  const reportContainer = useRef()
  const footerFiller = useRef()
  const reportFooter = useRef()
  const reportDisclaimer = useRef()
  const reportPaginationPageFooter = useRef()
  const overlayRef = useRef(null);

  // resize props
  const totalHeight = useRefState(null)
  const activeScale = useRefState(1)

  const resize = () => {
    if (!reportContainer.current || isPdfPrinter) return;

    const resizerContainer = reportContainer.current.parentNode;

    /* MOBILE */
    const zoomableContainer = resizerContainer.parentNode;
    const previousScrollLeftRatio =
      zoomableContainer.scrollLeft /
      (zoomableContainer.scrollWidth - zoomableContainer.clientWidth);
    const previousScrollTopRatio =
      zoomableContainer.scrollTop /
      (zoomableContainer.scrollHeight - zoomableContainer.clientHeight);
    resizerContainer.style.width = "";
    /* END MOBILE */

    // --- 🔧 Compute scale based on actual content width (scrollWidth)
    const pageWidth = Math.round(paperSize[0] * 96);
    const reportWidth =
      reportContainer.current.scrollWidth || reportContainer.current.clientWidth;

    activeScale.set(
      Math.min(
        resizerContainer.clientWidth / reportWidth,
        1
      ) * ((zoomDelta || 0) + 1)
    );

    // --- 🔧 Measure actual content height
    const naturalHeight = reportContainer.current.scrollHeight;
    totalHeight.set(naturalHeight);

    // --- 🔧 Apply visual scale
    reportContainer.current.style.transform = `scale(${activeScale.get()})`;
    reportContainer.current.style.transformOrigin = "left top";

    // --- 🔧 Sync heights and widths
    const scaledHeight = naturalHeight * activeScale.get();
    const expectedWidth = pageWidth;

    resizerContainer.style.height = scaledHeight + "px";
    resizerContainer.style.minHeight = scaledHeight + 8 + "px"; // small buffer to avoid clipping
    reportContainer.current.style.width = expectedWidth + "px";
    resizerContainer.style.width = expectedWidth * activeScale.get() + "px";

    /* MOBILE */
    if (isSmallDevice()) {
      resizerContainer.style.width = Math.round(
        resizerContainer.firstChild.getBoundingClientRect().width
      ) + "px";
      zoomableContainer.style.height = "";
      if (zoomDelta) {
        zoomableContainer.scrollLeft =
          previousScrollLeftRatio *
          (zoomableContainer.scrollWidth - zoomableContainer.clientWidth);
        zoomableContainer.scrollTop =
          previousScrollTopRatio *
          (zoomableContainer.scrollHeight - zoomableContainer.clientHeight);
      } else {
        zoomableContainer.style.height = zoomableContainer.scrollHeight + "px";
      }
    }
    /* END MOBILE */
  };

  useEffect(() => {
    resize()
  }, [zoomDelta]);

  useEffect(() => {
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  useEffect(() => {

    // Puppeteer/print-server already renders at full size (no scale), and this
    // hook was causing an extra blank page there. Skip the before/after print
    // adjustments when running in PDF mode.
    if (isPdfPrinter) return;
    
    const beforePrint = () => {
      if (!reportContainer.current) return;

      // Force real render before printing
      const el = reportContainer.current;
      el.getBoundingClientRect();
      const realHeight = el.scrollHeight + 200;

      // Temporarily remove transform
      el.style.transform = "none";
      el.style.height = realHeight + "px";
      el.style.width = Math.round(paperSize[0] * 96) + "px";

      // Ensure the parent has enough height
      const parent = el.parentNode;
      if (parent) {
        parent.style.height = realHeight + "px";
        parent.style.minHeight = realHeight + "px";
      }
    };

    const afterPrint = () => {
      if (!reportContainer.current) return;
      const el = reportContainer.current;
      el.style.transform = `scale(${activeScale.get()})`;
      el.style.transformOrigin = "left top";
      el.style.height = "auto";
    };

    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);

    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [activeScale, paperSize, isPdfPrinter]);

  useEffect(() => {
    isLogoLoaded.close();
    const imagesToLoad = [
      report.institutionDetails?.logoPath
        ? getReportResource(report.institutionDetails.logoPath, resourceOrigin)
        : organization.logoURL,
      ...visibleExperts
        .map(_.flow(_.get('picture'), s => getReportResource(s, resourceOrigin)))
        .filter(Boolean),
      ...signatures
        .map(_.flow(_.get('imagePath'), s => getReportResource(s, resourceOrigin)))
        .filter(Boolean),
      ...caseSummary
        .map(_.flow(_.get('value'), text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const imgs = Array.from(doc.querySelectorAll('img')).map(_.get('src'));
          return imgs.length > 0 ? imgs : null;
        }))
        .filter(Boolean)
        .flat(),
      ...additionalFields
        .map(_.flow(_.get('value'), text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const imgs = Array.from(doc.querySelectorAll('img')).map(_.get('src'));
          return imgs.length > 0 ? imgs : null;
        }))
        .filter(Boolean)
        .flat(),
      ...questionsAndAnswers
        .map(_.flow(_.get('answer'), text => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const imgs = Array.from(doc.querySelectorAll('img')).map(_.get('src'));
          return imgs.length > 0 ? imgs : null;
        }))
        .filter(Boolean)
        .flat(),
    ]
      .filter(Boolean)
      .map(imageSrc => 
        new Promise(resolve => {
          const img = document.createElement('img');
          img.onload = resolve;
          img.onerror = resolve;
          img.src = imageSrc;
        })
      );

    if (!imagesToLoad.length) {
      isLogoLoaded.open(); // nada pra carregar
      return;
    }

    Promise.all(imagesToLoad).then(() => isLogoLoaded.open());
  }, [
    // 👉 What actually changes the layout:
    report,
    organization,
    resourceOrigin,
  ]);

  const paginationRunRef = useRef(0);

  // Wait for web fonts to load before paginating
  const [fontsReady, setFontsReady] = useState(() => {
    if (typeof document === 'undefined') return true;
    // If the browser lacks the Font Loading API, consider it ready
    return !(document).fonts;
  });

  
  useEffect(() => {
    
    if (typeof document === 'undefined') return;
    const anyDoc = document;
    if (!anyDoc.fonts) {
      setFontsReady(true);
      return;
    }

    let cancelled = false;
    anyDoc.fonts.ready.then(() => {
      if (!cancelled) {
        setFontsReady(true);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  useLayoutEffect(() => {

    // Sometimes, the props migth change and we need to re-paginate
    // if (reportContainer.current?.dataset.paginated === "true") {
    //   // console.log("[pagination] DOM already paginated, skipping new run");
    //   return;
    // }
    
    if (isPreviewDoneRef.current) {
      clearPagination(reportContainer.current);
    }

    // 1) Missing refs or containers? Exit early.
    if (!reportContainer.current || !fieldsContainer.current) {
      // console.log('[pagination] skip: missing refs');
      return;
    }
  
    if (!fontsReady) {
      // console.log('[pagination] skip: fonts not ready yet');
      return;
    }
    if (!isLogoLoaded.isOpen) {
      // console.log('[pagination] skip: logos not loaded yet');
      return;
    }

    // 2) Already paginated successfully for this preview version?
    if (isPreviewDoneRef.current) {
      // console.log('[pagination] skip: already paginated');
      return;
    }

    const myRunId = ++paginationRunRef.current;
    let raf1, raf2;

    const runPagination = () => {
      if (myRunId !== paginationRunRef.current) return;
      if (!reportContainer.current) return;

      reportPaginationPageFooter.current.className = 'report-page-footer';
      compactHeaderContainer.current.style.display = 'block';
      footerFiller.current.style.height = '0';
      reportFooter.current.style.display = 'block';

      clearPagination(reportContainer.current);

      try {
        const ok = addPreviewPagination();   // addPreviewPagination will return boolean
        if (!ok) {
          return;
        }
      } catch (e) {
        console.error('Pagination failed', e);
        reportPaginationPageFooter.current.className = 'report-page-footer-failed';
        clearPagination(reportContainer.current);
        return;
      }

      if (!lastPageFooter) {
        reportFooter.current.style.display = 'none';
      }

      compactHeaderContainer.current.style.display = 'none';
      reportContainer.current.style.height = 'auto';

      if (!isPdfPrinter) {
        resize();
      }

      reportContainer.current.dataset.paginated = "true";

      // Mark as done to avoid repaginating unnecessarily
      isPreviewDoneRef.current = true;
      callIfFunction(previewReady, paperSize);
      if (overlayRef.current) {
        overlayRef.current.style.display = "none";
      }
    };

    raf1 = requestAnimationFrame(() => {
      // raf2 = requestAnimationFrame(runPagination);
      raf2 = requestAnimationFrame(() => {
        setTimeout(runPagination, 250);
      });
    });

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [
    // imagesLoaded,
    fontsReady,
    isLogoLoaded.isOpen,
    isPdfPrinter,
    lastPageFooter,
    paperSize,
    // ⚠️ Do not include previewReady here—each render creates a new ref
  ]);

  const addPreviewPagination = () => {
      const rc = reportContainer.current;
      const fc = fieldsContainer.current;
      if (!rc || !fc) {
        console.warn('[pagination] addPreviewPagination: missing refs');
        return false;
      }

      footerFiller.current.style.height = 0;

      rc.style.width = Math.round(paperSize[0] * 96) + 'px';
      rc.style.height =
        Math.round(paperSize[1] * 96) +
        parseInt(getComputedStyle(rc).borderTopWidth, 10) +
        parseInt(getComputedStyle(rc).borderBottomWidth, 10) +
        'px';

      const { height: paperHeight, top: containerTopWithBorder } = rc.getBoundingClientRect();
      if (!paperHeight || paperHeight < 100) {
        console.warn('[pagination] addPreviewPagination: paperHeight too small', { paperHeight });
        return false;
      }

      const reportContainerStyle = window.getComputedStyle(rc);
      const contentMarginTop =
        (parseFloat(reportContainerStyle.paddingTop, 10) || 0) +
        (parseFloat(reportContainerStyle.borderTopWidth, 10) || 0);
      const contentMarginBottom =
        (parseFloat(reportContainerStyle.paddingBottom, 10) || 0) +
        (parseFloat(reportContainerStyle.borderBottomWidth, 10) || 0);

      const contents = Array.prototype.slice.call(
        fc.querySelectorAll('.field-content,.field-content-block,.always-break-here'),
      );

      if (!contents.length) {
        console.warn('[pagination] addPreviewPagination: no contents to paginate');
        return false;
      }

      const lineHeight = getLineHeight(contents);
      if (!lineHeight) {
        console.warn('Pagination: lineHeight is 0, layout not ready');
        return false; // do nothing now; let the next cycle try
      }

      const minBreakSize = lineHeight * 3;
      const minContentSizeToBreak = lineHeight * 6;

      const compactHeaderHeight = compactHeaderContainer.current.offsetHeight;
      const reportFooterHeight = lastPageFooter ? reportFooter.current.offsetHeight : 0;

      const contentPageHeight = paperHeight - contentMarginBottom - contentMarginTop - compactHeaderHeight;
      const contentLastPageHeight = contentPageHeight - reportFooterHeight;

      const top = containerTopWithBorder + contentMarginTop + compactHeaderHeight;

      const { top: lastContentsTop, separators } = getSeparators(
        contents,
        top,
        {
          contentPageHeight,
          minBreakSize,
          contentLastPageHeight,
          canBeLastPage: true,
          lastPageIsCover: true,
          minContentSizeToBreak,
        },
        0,
        true,
      );

      // Extra debug: ensure we didn't hit the "no separators" case while an always-break-here is onscreen
      fc.querySelectorAll('.always-break-here, .has-broken');
      if (!separators.length) {
        console.warn('[pagination] no separators computed');
        // If you want to be very conservative, you can return false and leave everything untouched:
        return false;
      }

      const footerFillerBottom = footerFiller.current.getBoundingClientRect().bottom;
      const lastDiffVal = getLastDiff(separators);
      const footerDiff = Math.floor(
        lastContentsTop +
          contentPageHeight -
          footerFillerBottom -
          lastDiffVal +
          (separators.length === 0 ? -20 : 0),
      );

      // footerFiller.current.style.height = footerDiff + 'px';
      footerFiller.current.style.height = Math.max(0, footerDiff) + 'px';

      separators.reverse().forEach(({ diff, position, node }, i) => {
        const page = separators.length - 1 - i;

        // CASE 1: split inside a TEXT_NODE (manual)
        if (position !== undefined && node && node.nodeType === Node.TEXT_NODE) {
          const parent = node.parentNode;
          if (!parent) {
            return;
          }

          const fullText = node.textContent || '';
          const beforeText = fullText.slice(0, position);
          const afterText = fullText.slice(position);

          const beforeNode = beforeText ? document.createTextNode(beforeText) : null;
          const afterNode = afterText ? document.createTextNode(afterText) : null;

          if (beforeNode) {
            parent.insertBefore(beforeNode, node);
          }

          const filler = getEmptySpace(diff);
          const pageBreakNode = getPageBreak(
            intl,
            page,
            separators.length,
            hasCoverPages && page === 0
          );
          const headerNode = getPageHeader(i === 0);

          parent.insertBefore(filler, node);
          parent.insertBefore(pageBreakNode, node);
          parent.insertBefore(headerNode, node);

          if (afterNode) {
            parent.insertBefore(afterNode, node);
          }

          parent.removeChild(node);

          return;
        }

        // CASE 2: no position -> break before the entire node
        if (position === undefined) {
          
          if (!node || !node.parentNode) {
            console.warn('[pagination] node with no parent on BEFORE NODE', { page });
            return;
          }

          node.parentNode.insertBefore(getEmptySpace(diff), node);
          node.parentNode.insertBefore(
            getPageBreak(intl, page, separators.length, hasCoverPages && page === 0),
            node
          );
          node.parentNode.insertBefore(getPageHeader(i === 0), node);
          return;
        }

        // CASE 3: position set but node is not a TextNode
        if (!node) return;

        const range = document.createRange();
        range.setStart(node, position);
        range.setEnd(node, position);

        const filler = getEmptySpace(diff);
        const pageBreakNode = getPageBreak(
          intl,
          page,
          separators.length,
          hasCoverPages && page === 0
        );
        const headerNode = getPageHeader(i === 0);

        range.insertNode(headerNode);
        range.insertNode(pageBreakNode);
        range.insertNode(filler);
      });
      if (separators.length === 0) {
        reportFooter.current.style.display = 'none';
      }

    return true; // 👈 indicate success
  };

  const getPageHeader = (invisible) => {
    const clone = compactHeaderContainer.current.cloneNode(true)
    const height = compactHeaderContainer.current.offsetHeight
    const container = document.createElement('div')
    container.style.height = height + 'px'
    container.style.marginBottom = '2px'
    if (invisible) {
      container.style.visibility = 'hidden'
    }
    container.className = 'report-header-fix-container'
    container.appendChild(clone)
    return container
  }


  const patientName = (report.patientInfo.firstName || report.patientInfo?.middleName || report.patientInfo.lastName)
      ? <>{report.patientInfo.firstName} {report.patientInfo?.middleName} {report.patientInfo.lastName}</>
      : report.patientInfo.patientName

  return ( <>
    <PrintReportStyle />
    <ReportOverlay ref={overlayRef}>
      <ReportOverlayTopDiv>
        <Loading size={40} />
      </ReportOverlayTopDiv>
    </ReportOverlay>
    <PageResizerContainer>
        <div 
          ref={reportContainer} 
          className="report-page"
          // className={`report-page ${shouldHide ? "report-invisible" : "report-visible"}`}
        >
          {
            REPORT_HEADERS[reportLayout.headerLayout](report.institutionDetails,organization,true,resourceOrigin)
          }
          <FullPage paperSize={paperSize}><div className="cover-filler">
            <CoverMainWrapper>
              <CoverTitle enhancedContrastColor={reportData?.customColors?.enhancedContrastColor ? reportData?.customColors?.enhancedContrastColor : false}> 
                <h1 className="title" id='report-title'>
                  {reportData.title || <FormattedMessage
                    defaultMessage="Case report"
                    description="Print report case report title"
                    />}
                </h1>
                {organization?.reportShowEhrOrderNumber && <span className="order-number"><FormattedMessage defaultMessage="Order number" description="Order number report label"/>: {
                  report?.ehrInfo?.orderNumber && report?.ehrInfo?.orderNumber !== "" 
                  ? report?.ehrInfo?.orderNumber 
                  : "-"
                }</span>}
                <div className="report-date">
                  {
                    formatDateNoUTC(report.whenReturnedEpochMilli ? new Date(report.whenReturnedEpochMilli) : new Date(), DATE_FORMAT_STR[reportLayout.dateFormat], i18nDateMessages(intl))
                  }
                  <span className="case-id" id="case-id"> (<FormattedMessage
                    defaultMessage="Case ID"
                    description="Case id label"
                    />: {report.id})</span>
                </div>
                <div className="patient-info">
                  <div className="name" id="patient-name">
                    <label style={{color: "#636362"}}><FormattedMessage defaultMessage="Patient" description="patient label"/></label>
                    <div><strong className='fs-exclude'>
                      {patientName}
                    </strong></div>
                  </div>
                  {organization?.reportShowEhrMRN && <div className="mrn" id="mrn">
                    <label style={{color: "#636362"}}><FormattedMessage defaultMessage="MRN" description="MRN report label"/>:</label>
                    <div><strong className='fs-exclude'>
                      {report?.ehrInfo?.mrn && report?.ehrInfo?.mrn !== "" ? report?.ehrInfo?.mrn : "-"}
                    </strong></div>
                  </div>}
                  
                  {
                    patientDoB && <div className="dob">
                    <label style={{color: "#636362"}}><FormattedMessage
                      defaultMessage="Date of birth"
                      description="Date of birth label"
                    /></label>
                    <div><strong className='fs-exclude'>
                      {formatDateNoUTC(patientDoB, defaultDateFormat(intl))} <br></br><FormattedMessage
                      defaultMessage="({age} years old)"
                      description="Report preview patient age"
                      values={{ age: calculateAge(patientDoB) }}
                    />
                    </strong></div>
                  </div>
                  }
                </div>
              </CoverTitle>
              {reportData.coverNote &&
                <CoverNote isPreview>
                  {reportData.coverNote}
                </CoverNote>
              }
            </CoverMainWrapper>
            <CoverFooter isPreview>
              <ReportByPurview>
                <FormattedMessage
                  defaultMessage="Created with Purview Expert View™"
                  description="Report preview created with purview"
                />
              </ReportByPurview>
              {
                disclaimer &&
                <ReportField isDisclaimer isPreview>
                  <div className="field-content" ref={reportDisclaimer}>
                    <strong><FormattedMessage
                      defaultMessage="Disclaimer:"
                      description="Report preview disclaimer label"
                    /></strong> {disclaimer}
                  </div>
                </ReportField>
              }
            </CoverFooter>
          </div></FullPage>
          <div ref={fieldsContainer}>
            <div className="always-break-here"/>
            {
              visibleExperts.length > 0 && <>
                <section role='region' aria-labelledby="Experts">
                  <TopSectionHeading enhancedContrastColor={reportData?.customColors?.enhancedContrastColor ? reportData?.customColors?.enhancedContrastColor : false}>
                    <h2 id='Experts'><FormattedMessage description='Preview expert physician title' defaultMessage='Expert physician' /></h2>
                    <p><FormattedMessage
                      defaultMessage="This expert review was provided by:"
                      description="Report, expert review provided by label"
                    /></p>
                  </TopSectionHeading>
                  {visibleExperts.map((expert) => <ExpertProfileItem resourceOrigin={resourceOrigin} showEdit={expert.showEdit} expert={expert} />)}
                  <div className="always-break-here"/>
                </section>
              </>
            }
            <section role='region' aria-labelledby="Expert review">
              <TopSectionHeading enhancedContrastColor={reportData?.customColors?.enhancedContrastColor ? reportData?.customColors?.enhancedContrastColor : false}>
                <h3 id='expert-review'><FormattedMessage
                  defaultMessage="Expert review"
                  description="Report preview expert review title"
                /></h3>
              </TopSectionHeading>
            </section>
            <section role='region' aria-labelledby="Summary">
              {clinicalSummary.map(field =>
                <ReportField key={field.label} className="NODE-TO-BREAK" isPreview>
                  <div className="field-heading fs-exclude">
                    <h4>{field.label}</h4>
                  </div>
                  {field.value ?
                    <div className="field-content fs-exclude" dangerouslySetInnerHTML={{__html: sanitizeHtml(safeNewLineToDoubleBr(field.value))}}/>
                    : <NotProvided css="margin-top: .5rem; display: block;"/>
                  }
                </ReportField>
              )}
              {caseSummary.map(field =>
                <ReportField key={field.label} className="NODE-TO-BREAK" isPreview>
                  <div className="field-heading fs-exclude">
                    <h4>{field.label}</h4>
                  </div>
                  {field.value ?
                    // <div className="field-content" dangerouslySetInnerHTML={{__html: safeNewLineToDoubleBr(field.value)}}/>
                    // <div className="field-content" dangerouslySetInnerHTML={{__html: isRichText(field.value) ? field.value : safeNewLineToDoubleBr(field.value)}}/>
                    <RichTextDisplay className={"field-content fs-exclude"} content={field.value} useSafeNewLine={!isRichText(field.value)} />
                    : <NotProvided css="margin-top: .5rem; display: block;"/>
                  }
                </ReportField>
              )}
            </section>
            {questionsAndAnswers.length > 0 && <>
              <section role='region' aria-labelledby="Questions & answers">
                <ReportField>
                  <div className="field-heading">
                    <h4><FormattedMessage
                      defaultMessage="Questions & answers"
                      description="Report preview questions and answers title"
                    /></h4>
                  </div>
                </ReportField>
                {questionsAndAnswers.map(({question,answer},i)=>
                  <div key={question+i} className="NODE-TO-BREAK question-and-answer">
                    <div style={{color: "#636362"}}><FormattedMessage
                      defaultMessage="Question"
                      description="Report preview question label"
                    />:</div>
                    <div className="field-content question fs-exclude" dangerouslySetInnerHTML={{__html: sanitizeHtml(question)}}/>
                    {/* {answer && <div className="field-content answer" dangerouslySetInnerHTML={{__html: safeNewLineToDoubleBr(answer)}}/>} */}
                    {answer && <div style={{color: "#636362", marginTop: "0.5rem" }}><FormattedMessage
                      defaultMessage="Answer"
                      description="Report preview answer label"
                      />:</div>}
                    {answer && <RichTextDisplay className="field-content answer fs-exclude" content={answer} useSafeNewLine={!isRichText(answer)}/>}
                  </div>)}
              </section>
            </>}

            {additionalFields.length > 0 &&
              <ReportField>
                <section role='region' aria-labelledby="Additional information">
                  <div className="field-heading">
                    <h4><FormattedMessage
                      defaultMessage="Additional information"
                      description="Report preview additional information title"
                    /></h4>
                  </div>
                </section>
              </ReportField>
            }
            {additionalFields.map(field =>
              <ReportField key={field.label} className="NODE-TO-BREAK" isPreview subItem>
                <div className="field-heading fs-exclude">
                  {/* <h4>{field.label}</h4> */}
                  <h4><RichTextDisplay content={field.label} /></h4>
                </div>
                {field.value ?
                  // <div className="field-content" dangerouslySetInnerHTML={{__html: isRichText(field.value) ? field.value : safeNewLineToDoubleBr(field.value)}}/>
                  <RichTextDisplay className="field-content fs-exclude" content={field.value} useSafeNewLine={!isRichText(field.value)} />
                  : <NotProvided css="margin-top: .5rem; display: block;"/>
                }
              </ReportField>
            )}
            {signatures.length >= 1 && <div className='NODE-TO-BREAK signature-block'>
              <ReportField className="field-content">
                <div className="field-heading">
                  <h4><FormattedMessage
                    defaultMessage="Signatures"
                    description="Report preview signatures title"
                  /></h4>
                </div>
              </ReportField>
              <InsertedSignaturesWrapper>
                {
                  signatures.map(signature =>
                    <SignatureItem
                      className='field-content signature-item'
                      signature={signature}
                      resourceOrigin={resourceOrigin}
                      isPreview
                    />)
                }
              </InsertedSignaturesWrapper>
              </div>
            }
            <div className="always-break-here"/>
          </div>
          <div className="report-compact-header" ref={compactHeaderContainer}>
            <div className="report-compact-header-content">
              <span>{organization.name}</span>
              <span className='fs-exclude'><FormattedMessage description="patient label" defaultMessage="Patient"/>: {patientName} {patientDoB && <>- <FormattedMessage
                  defaultMessage="DOB"
                  description="Report preview, date of birth label"
                />: {formatDateNoUTC(patientDoB, defaultDateFormat(intl))}</>}</span>
            </div>
            <div className="report-compact-header-margin"/>
          </div>
          
          <div className="footer-filler" ref={footerFiller}>
            <BackCoverNotesWrapper>
              <ThankNote id='thank-you-note' enhancedContrastColor={reportData?.customColors?.enhancedContrastColor ? reportData?.customColors?.enhancedContrastColor : false}>
                <FormattedMessage
                  defaultMessage="Thank you for choosing"
                  description="Report preview thank you note"
                /><br/>{report?.institutionDetails?.name ? report?.institutionDetails?.name : organization?.name}</ThankNote>
              {reportData.lastPageNote &&
                <MarketingMessage>
                  {reportData.lastPageNote}
                </MarketingMessage>
              }
            </BackCoverNotesWrapper>

            <BackCoverMainWrapper>
              {/* <CoverMainWrapper> */}
              {
                REPORT_HEADERS[reportLayout.headerLayout](report.institutionDetails,organization,true,resourceOrigin)
              }
              {/* </CoverMainWrapper> */}
            </BackCoverMainWrapper>
            <div className="report-footer" ref={reportFooter}>
              <ReportByPurview>
                <FormattedMessage
                  defaultMessage="Created with Purview Expert View™"
                  description="Report preview created with purview"
                />
              </ReportByPurview>
              {
                disclaimer &&
                <ReportField isDisclaimer isPreview>
                  <div className="field-content" ref={reportDisclaimer}>
                    <strong><FormattedMessage
                      defaultMessage="Disclaimer"
                      description="Report preview disclaimer label"
                    />:</strong> {disclaimer}
                  </div>
                </ReportField>
              }
            </div>
          </div>
          <div hidden className="report-page-footer" ref={reportPaginationPageFooter}/>
        </div>
    </PageResizerContainer>
  </>)
}
